import { apiFetch } from '@/lib/api/client';
import type { ApiPostCardItem, ApiPostDetailResp, ApiPostListResp } from '@/lib/community/community-types-api';
import { transformPostCard, transformPostDetail } from '@/lib/community/community-transforms';
import type { CommunityPost } from '@/lib/community/types';

const D = '/discourse/api/v1';
const S = '/skill/api/v1';
const ADMIN_SKILL = '/admin/api/v1/skill';
const ADMIN = '/admin/api/v1/college';

export interface CreatorListResp<T> {
	total: number;
	page: number;
	pageSize: number;
	items: T[];
}

export async function fetchCreatorPosts(userId: string, page = 1, pageSize = 20): Promise<CreatorListResp<CommunityPost>> {
	const resp = await apiFetch<ApiPostListResp>(`${D}/post/user/${userId}`, {
		params: { page, pageSize },
	});
	return {
		total: resp.total,
		page,
		pageSize,
		items: resp.list.map((item: ApiPostCardItem) => transformPostCard(item)),
	};
}

export async function fetchCreatorPostDetail(id: string): Promise<CommunityPost> {
	const resp = await apiFetch<ApiPostDetailResp>(`${D}/post/${id}`);
	return transformPostDetail(resp);
}

export async function updateCreatorPost(id: string, body: { title: string; content: string; coverImage?: string }): Promise<void> {
	await apiFetch(`${D}/post/${id}`, {
		method: 'PUT',
		body,
	});
}

export async function deleteCreatorPost(id: string): Promise<void> {
	await apiFetch(`${D}/post/${id}`, { method: 'DELETE' });
}

export interface PublishedSkillItem {
	id: number;
	skillKey: string;
	version: string;
	name: string;
	description: string;
	category: string;
	tags: string;
	level: string;
	source: string;
	author: string;
	repoUrl: string;
	archivePath: string;
	directory: string;
	isPaid: number;
	price: number;
	publisherType: string;
	publisherId: string;
	metadata: Record<string, unknown>;
	content: string;
	collectNum: number;
	likeNum: number;
	downloadNum: number;
	useNum: number;
	isCollect: boolean;
	isLike: boolean;
	isDownload: boolean;
	isUse: boolean;
	createdAt: string;
	updatedAt: string;
}

interface PublishedSkillResp {
	total: number;
	page: number;
	pageSize: number;
	items: PublishedSkillItem[];
}

export async function fetchPublishedSkills(page = 1, pageSize = 20): Promise<CreatorListResp<PublishedSkillItem>> {
	const resp = await apiFetch<PublishedSkillResp>(`${S}/my/published`, {
		params: { page, pageSize },
	});
	return {
		total: Number(resp.total ?? 0),
		page: resp.page ?? page,
		pageSize: resp.pageSize ?? pageSize,
		items: resp.items ?? [],
	};
}

export async function fetchPublishedSkillDetail(id: number | string): Promise<PublishedSkillItem> {
	return apiFetch<PublishedSkillItem>(`${S}/detail/${id}`);
}

export async function updatePublishedSkill(skill: Partial<PublishedSkillItem> & { id: number }): Promise<void> {
	await apiFetch(`${ADMIN_SKILL}/`, {
		method: 'PUT',
		body: skill,
	});
}

export async function deletePublishedSkill(id: number | string): Promise<void> {
	await apiFetch(`${ADMIN_SKILL}/${id}`, { method: 'DELETE' });
}

export interface MyCollegeContentResp {
	courses?: { total: number; list: MyCollegeCourseItem[] };
	chapters?: { total: number; list: MyCollegeChapterItem[] };
	steps?: { total: number; list: MyCollegeStepItem[] };
}

export interface MyCollegeCourseItem {
	id: number;
	seriesId: number;
	title: string;
	intro: string;
	description: string;
	coverImage: string;
	sortOrder: number;
	status: number;
	createdAt: string;
}

export type UpdateMyCollegeCourseRequest = Partial<Pick<MyCollegeCourseItem, 'title' | 'intro' | 'description' | 'coverImage' | 'sortOrder' | 'status'>> & {
	id: number;
};

export interface MyCollegeChapterItem {
	id: number;
	courseId: number;
	title: string;
	intro: string;
	objective: string;
	description: string;
	sortOrder: number;
	status: number;
	learned: boolean;
	createdAt: string;
}

export interface MyCollegeStepItem {
	id: number;
	chapterId: number;
	title: string;
	contentType: 'md' | 'image' | 'video' | string;
	content: string;
	sortOrder: number;
	createdAt: string;
}

export async function fetchMyCollegeContent(page = 1, pageSize = 20, type?: 'course' | 'chapter' | 'step'): Promise<MyCollegeContentResp> {
	return apiFetch<MyCollegeContentResp>(`${D}/college/my-content`, {
		params: { page, pageSize, type },
	});
}

export async function updateMyCollegeCourse(data: UpdateMyCollegeCourseRequest): Promise<void> {
	const { id, ...body } = data;
	await apiFetch(`${ADMIN}/course/${id}`, {
		method: 'PUT',
		body,
	});
}

export async function deleteMyCollegeCourse(id: number | string): Promise<void> {
	await apiFetch(`${ADMIN}/course/${id}`, {
		method: 'DELETE',
	});
}
